// @ts-check
// Barra de filtros do painel: período (atalhos e intervalo personalizado), unidade, consultor e período letivo.
// O estado vai para a URL, então o link aberto por outra pessoa mostra os mesmos números.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, LightButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findRecordsByIds } from '../../data/rest.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { formatFullName, getRecordLabel } from '../../lib/field-metadata.js';
import { formatDate } from '../../lib/format.js';
import { PERIOD_PRESETS, resolvePeriodRange } from '../../lib/dashboard-filters.js';
import { RecordPickerList } from '../object-record/record-picker.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/dashboard-filters.js').DashboardFilterState} DashboardFilterState
 */

/** @type {Map<string, string>} */
const recordLabelCache = new Map();

/**
 * Nome do registro filtrado (o id vem da URL; o nome é buscado uma vez).
 * @param {string} objectNameSingular
 * @param {string | null} recordId
 */
function useRecordLabel(objectNameSingular, recordId) {
  const cacheKey = `${objectNameSingular}:${recordId}`;
  const [label, setLabel] = useState(() => (recordId ? recordLabelCache.get(cacheKey) ?? null : null));
  useEffect(() => {
    if (!recordId) return setLabel(null);
    if (recordLabelCache.has(cacheKey)) return setLabel(recordLabelCache.get(cacheKey) ?? null);
    const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
    if (!objectMetadata) return undefined;
    let isCancelled = false;
    findRecordsByIds(objectMetadata, [recordId])
      .then(([record]) => {
        const recordLabel = record ? getRecordLabel(objectMetadata, record) : '';
        recordLabelCache.set(cacheKey, recordLabel);
        if (!isCancelled) setLabel(recordLabel);
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [cacheKey]);
  return label;
}

/**
 * @param {{ icon: string, label: string, value?: string | null, isActive: boolean, isOpen: boolean, onToggle: () => void, onClear?: () => void,
 *   anchorRef: { current: HTMLDivElement | null }, children: any, width?: number }} props
 */
function FilterChip({ icon, label, value, isActive, isOpen, onToggle, onClear, anchorRef, children, width = 240 }) {
  return html`<div class="dashboard-filter" ref=${anchorRef}>
    ${isActive
      ? html`<div class="sort-or-filter-chip">
          <button type="button" class="sort-or-filter-chip-main" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${onToggle}>
            <${Icon} name=${icon} size=${14} />
            <span class="sort-or-filter-chip-label">${label}:</span>
            <span class="sort-or-filter-chip-value">${value ?? '...'}</span>
          </button>
          ${onClear
            ? html`<button type="button" class="sort-or-filter-chip-remove" aria-label=${t('dashboards.filters.clear', { filter: label })} onClick=${onClear}>
                <${Icon} name="IconX" size=${14} />
              </button>`
            : null}
        </div>`
      : html`<button type="button" class="view-bar-add-filter" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${onToggle}>
          <${Icon} name="IconPlus" size=${14} /><span>${label}</span>
        </button>`}
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${onToggle} width=${width}>${isOpen ? children : null}<//>
  </div>`;
}

/**
 * @param {{ state: DashboardFilterState, onChange: (changes: Partial<DashboardFilterState>) => void, onClose: () => void }} props
 */
function PeriodMenu({ state, onChange, onClose }) {
  const [isCustom, setIsCustom] = useState(state.period === 'CUSTOM');
  const initialRange = resolvePeriodRange(state.period, state);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  if (isCustom) {
    return html`
      <${DropdownMenuHeader} title=${t('dashboards.periods.CUSTOM')} onBack=${() => setIsCustom(false)} />
      <form class="dashboard-period-custom" onSubmit=${(/** @type {Event} */ event) => {
        event.preventDefault();
        if (!from || !to) return;
        onChange({ period: 'CUSTOM', from: from <= to ? from : to, to: from <= to ? to : from });
        onClose();
      }}>
        <label class="form-field">
          <span class="form-field-label">${t('dashboards.filters.from')}</span>
          <input class="text-input" type="date" value=${from} required onInput=${(event) => setFrom(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
        <label class="form-field">
          <span class="form-field-label">${t('dashboards.filters.to')}</span>
          <input class="text-input" type="date" value=${to} required onInput=${(event) => setTo(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
        <${Button} title=${t('dashboards.filters.apply')} type="submit" accent="blue" size="small" fullWidth=${true} />
      </form>
    `;
  }
  return html`<${DropdownMenuItems}>
    ${PERIOD_PRESETS.map((period) => html`<${MenuItem}
      key=${period}
      text=${t(`dashboards.periods.${period}`)}
      contextualText=${period === 'CUSTOM' ? undefined : formatRange(resolvePeriodRange(period))}
      selected=${state.period === period}
      hasSubMenu=${period === 'CUSTOM'}
      onClick=${() => {
        if (period === 'CUSTOM') return setIsCustom(true);
        onChange({ period, from: null, to: null });
        onClose();
      }}
    />`)}
  <//>`;
}

/**
 * @param {{ from: string, to: string }} range
 */
export function formatRange(range) {
  if (range.from === range.to) return formatDate(range.from);
  const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
  const fromLabel = sameYear ? formatDate(range.from).slice(0, 5) : formatDate(range.from);
  return t('dashboards.filters.range', { from: fromLabel, to: formatDate(range.to) });
}

/**
 * @param {{ selectedId: string | null, onSelect: (id: string | null) => void }} props
 */
function ConsultantMenu({ selectedId, onSelect }) {
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  const members = workspaceMembersState.value ?? [];
  return html`<${DropdownMenuItems} hasMaxHeight=${true}>
    <${MenuItem} icon="IconCircleOff" accent="placeholder" text=${t('dashboards.filters.allConsultants')} selected=${!selectedId} onClick=${() => onSelect(null)} />
    ${members.length > 0 ? html`<${DropdownMenuSeparator} />` : null}
    ${members.map((member) => {
      const name = formatFullName(member.name) || member.userEmail || '';
      return html`<${MenuItem}
        key=${member.id}
        text=${name}
        selected=${selectedId === member.id}
        leftComponent=${html`<${Avatar} placeholder=${name} seed=${member.id} size="md" type="rounded" avatarUrl=${member.avatarUrl ?? null} />`}
        onClick=${() => onSelect(member.id)}
      />`;
    })}
  <//>`;
}

/**
 * @param {{ state: DashboardFilterState, onChange: (changes: Partial<DashboardFilterState>) => void, isDefault: boolean,
 *   canSaveDefault: boolean, onSaveDefault: () => void, onReset: () => void }} props
 */
export function DashboardFilterBar({ state, onChange, isDefault, canSaveDefault, onSaveDefault, onReset }) {
  const [openFilter, setOpenFilter] = useState(/** @type {string | null} */ (null));
  const periodRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const campusRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const consultantRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const academicPeriodRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const campusLabel = useRecordLabel('campus', state.campusId);
  const academicPeriodLabel = useRecordLabel('academicPeriod', state.academicPeriodId);
  const hasCampus = objectsByNameSingular.value.has('campus');
  const hasAcademicPeriod = objectsByNameSingular.value.has('academicPeriod');
  const members = workspaceMembersState.value;
  useEffect(() => {
    if (state.ownerWorkspaceMemberId) loadWorkspaceMembers();
  }, [state.ownerWorkspaceMemberId]);
  const consultant = members?.find((member) => member.id === state.ownerWorkspaceMemberId);
  const toggle = (/** @type {string} */ name) => setOpenFilter((current) => (current === name ? null : name));
  const close = () => setOpenFilter(null);
  const range = resolvePeriodRange(state.period, state);

  return html`<div class="dashboard-filter-bar" role="toolbar" aria-label=${t('dashboards.filters.title')}>
    <div class="dashboard-filter-list">
      <${FilterChip}
        icon="IconCalendar"
        label=${state.period === 'CUSTOM' ? t('dashboards.filters.period') : t(`dashboards.periods.${state.period}`)}
        value=${formatRange(range)}
        isActive=${true}
        isOpen=${openFilter === 'period'}
        onToggle=${() => toggle('period')}
        anchorRef=${periodRef}
        width=${260}
      >
        <${PeriodMenu} state=${state} onChange=${onChange} onClose=${close} />
      <//>
      ${hasCampus
        ? html`<${FilterChip}
            icon="IconBuildingCommunity"
            label=${t('dashboards.filters.campus')}
            value=${campusLabel}
            isActive=${Boolean(state.campusId)}
            isOpen=${openFilter === 'campus'}
            onToggle=${() => toggle('campus')}
            onClear=${() => onChange({ campusId: null })}
            anchorRef=${campusRef}
            width=${260}
          >
            <${RecordPickerList}
              objectNameSingular="campus"
              selectedIds=${state.campusId ? [state.campusId] : []}
              emptyLabel=${t('dashboards.filters.allCampuses')}
              onSelect=${(record) => {
                if (record) recordLabelCache.set(`campus:${record.id}`, record.label);
                onChange({ campusId: record?.id ?? null });
                close();
              }}
            />
          <//>`
        : null}
      <${FilterChip}
        icon="IconUserCircle"
        label=${t('dashboards.filters.consultant')}
        value=${consultant ? formatFullName(consultant.name) || consultant.userEmail : null}
        isActive=${Boolean(state.ownerWorkspaceMemberId)}
        isOpen=${openFilter === 'consultant'}
        onToggle=${() => toggle('consultant')}
        onClear=${() => onChange({ ownerWorkspaceMemberId: null })}
        anchorRef=${consultantRef}
        width=${240}
      >
        <${ConsultantMenu} selectedId=${state.ownerWorkspaceMemberId} onSelect=${(id) => {
          onChange({ ownerWorkspaceMemberId: id });
          close();
        }} />
      <//>
      ${hasAcademicPeriod
        ? html`<${FilterChip}
            icon="IconCalendarStats"
            label=${t('dashboards.filters.academicPeriod')}
            value=${academicPeriodLabel}
            isActive=${Boolean(state.academicPeriodId)}
            isOpen=${openFilter === 'academicPeriod'}
            onToggle=${() => toggle('academicPeriod')}
            onClear=${() => onChange({ academicPeriodId: null })}
            anchorRef=${academicPeriodRef}
            width=${260}
          >
            <${RecordPickerList}
              objectNameSingular="academicPeriod"
              selectedIds=${state.academicPeriodId ? [state.academicPeriodId] : []}
              emptyLabel=${t('dashboards.filters.allAcademicPeriods')}
              onSelect=${(record) => {
                if (record) recordLabelCache.set(`academicPeriod:${record.id}`, record.label);
                onChange({ academicPeriodId: record?.id ?? null });
                close();
              }}
            />
          <//>`
        : null}
    </div>
    ${!isDefault
      ? html`<div class="dashboard-filter-actions">
          <${LightButton} title=${t('dashboards.filters.reset')} accent="tertiary" onClick=${onReset} />
          ${canSaveDefault ? html`<${Button} title=${t('dashboards.filters.saveDefault')} size="small" variant="secondary" accent="blue" onClick=${onSaveDefault} />` : null}
        </div>`
      : null}
  </div>`;
}
